import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, googleProvider } from '../lib/firebase';
import { 
  onAuthStateChanged, 
  signInWithPopup, 
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { BACKEND_URL } from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 10000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(loadingTimeout);
      
      if (firebaseUser) {
        // Check if email verification is required (for email/password users)
        const isEmailPasswordUser = firebaseUser.providerData.some(
          provider => provider.providerId === 'password'
        );
        
        if (isEmailPasswordUser && !firebaseUser.emailVerified) {
          // User signed up with email but hasn't verified yet
          setEmailVerificationRequired(true);
          setUser({
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            firebase_uid: firebaseUser.uid,
            emailVerified: false
          });
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        setEmailVerificationRequired(false);
        
        try {
          const idToken = await firebaseUser.getIdToken();
          
          const backendUrl = BACKEND_URL;
          let apiUrl = backendUrl;
          if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
            apiUrl = '';
          }
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);
          
          try {
            const response = await fetch(`${apiUrl}/api/auth/firebase-login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              credentials: 'include',
              signal: controller.signal
            });

            clearTimeout(timeoutId);
            
            if (response.ok) {
              const userData = await response.json();
              setUser(userData.user);
              setIsAuthenticated(true);
            } else {
              setUser({
                email: firebaseUser.email,
                name: firebaseUser.displayName,
                firebase_uid: firebaseUser.uid,
                role: 'USER'
              });
              setIsAuthenticated(true);
            }
          } catch (fetchError) {
            clearTimeout(timeoutId);
            setUser({
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              firebase_uid: firebaseUser.uid,
              role: 'USER'
            });
            setIsAuthenticated(true);
          }
        } catch (error) {
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setEmailVerificationRequired(false);
      }
      
      setLoading(false);
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, []);

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      setLoading(false);
      
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Login cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by your browser. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error('Authentication Error: This domain is not authorized.');
      } else {
        throw new Error(error.message);
      }
    }
  };

  const signUpWithEmail = async (email, password, displayName) => {
    try {
      setLoading(true);
      
      // Create user account
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Set the user's display name
      if (displayName) {
        await updateProfile(result.user, {
          displayName: displayName
        });
      }
      
      // Send verification email with redirect URL
      const actionCodeSettings = {
        url: window.location.origin + '/?verified=true',
        handleCodeInApp: false
      };
      await sendEmailVerification(result.user, actionCodeSettings);
      
      setLoading(false);
      
      return {
        success: true,
        message: 'Account created! Please check your email to verify your account.',
        user: result.user
      };
    } catch (error) {
      setLoading(false);
      
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password should be at least 6 characters.');
      } else {
        throw new Error(error.message);
      }
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      setLoading(true);
      
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if email is verified
      if (!result.user.emailVerified) {
        setLoading(false);
        throw new Error('Please verify your email before signing in. Check your inbox for the verification link.');
      }
      
      // Auth state change will handle the rest
      return { success: true };
    } catch (error) {
      setLoading(false);
      
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      } else if (error.message) {
        throw error;
      } else {
        throw new Error('Failed to sign in. Please try again.');
      }
    }
  };

  const resendVerificationEmail = async () => {
    try {
      if (auth.currentUser && !auth.currentUser.emailVerified) {
        const actionCodeSettings = {
          url: window.location.origin + '/?verified=true',
          handleCodeInApp: false
        };
        await sendEmailVerification(auth.currentUser, actionCodeSettings);
        return { success: true, message: 'Verification email sent! Please check your inbox.' };
      }
      throw new Error('No pending verification found.');
    } catch (error) {
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Please wait a moment before requesting another email.');
      }
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'Password reset email sent! Check your inbox.' };
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else {
        throw new Error('Failed to send reset email. Please try again.');
      }
    }
  };

  const logout = async () => {
    try {
      const backendUrl = BACKEND_URL;
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }
      
      await fetch(`${apiUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {});
      
      await signOut(auth);
    } catch (error) {
      // Silently handle logout errors
    }
  };

  const getIdToken = async () => {
    try {
      if (user && user.email === 'test@admin.com') {
        return 'mock-admin-token';
      }
      
      if (auth.currentUser) {
        return await auth.currentUser.getIdToken();
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const refreshUserProfile = async () => {
    try {
      if (!auth.currentUser) return null;
      
      const idToken = await getIdToken();
      if (!idToken) return null;
      
      const backendUrl = BACKEND_URL;
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }
      
      const response = await fetch(`${apiUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const updatedUserData = await response.json();
        setUser(updatedUserData.user || updatedUserData);
        return updatedUserData;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const testLogin = async () => {
    try {
      setLoading(true);
      
      const mockUser = {
        id: 'test-user-123',
        firebase_uid: 'test-firebase-uid',
        email: 'test@admin.com',
        name: 'Test Admin',
        is_designer: true,
        designer_status: 'approved',
        role: 'SUPER_ADMIN'
      };
      
      setUser(mockUser);
      setIsAuthenticated(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    emailVerificationRequired,
    loginWithGoogle,
    signUpWithEmail,
    loginWithEmail,
    resendVerificationEmail,
    resetPassword,
    logout,
    getIdToken,
    refreshUserProfile,
    testLogin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
